"use client";

import { motion } from "framer-motion";
import { Check } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Plan {
  _id: string;
  planName: string;
  noOfCompany: number;
  planType: string;
  duration: number;
  amount: number;
  createdDate: string;
}

interface PlanCardViewProps {
  plans: Plan[];
  purchasing: string | null;
  handlePurchase: (planId: string) => Promise<void>;
  getPlanIcon: (planType: string) => LucideIcon;
  getPlanColor: (index: number) => string;
}

export default function PlanCardView({
  plans,
  purchasing,
  handlePurchase,
  getPlanIcon,
  getPlanColor,
}: PlanCardViewProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {plans.map((plan, index) => {
        const Icon = getPlanIcon(plan.planType);
        const colorClass = getPlanColor(index);
        return (
          <motion.div
            key={plan._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden card-hover"
          >
            <div className={`bg-gradient-to-r ${colorClass} p-6 text-white`}>
              <div className="flex items-center justify-between mb-4">
                <Icon className="w-8 h-8" />
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                  {plan.planType}
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-2">{plan.planName}</h3>
              <div className="flex items-baseline">
                <span className="text-3xl font-bold">
                  ₹{plan.amount.toLocaleString()}
                </span>
                <span className="text-white/80 ml-2">
                  /{plan.duration} days
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">
                    {plan.noOfCompany}{" "}
                    {plan.noOfCompany === 1 ? "Company" : "Companies"}
                  </span>
                </div>
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">
                    {plan.duration} days validity
                  </span>
                </div>
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">24/7 Customer Support</span>
                </div>
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Advanced Analytics</span>
                </div>
                {plan.planType === "Multi" && (
                  <div className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">
                      Multi-company Management
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => handlePurchase(plan._id)}
                disabled={purchasing === plan._id}
                className={`w-full bg-gradient-to-r ${colorClass} text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
              >
                {purchasing === plan._id ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Purchase Plan"
                )}
              </button>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
